import styled from "@emotion/styled";
import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import { find, has } from "lodash";
import {
    MdPermDataSetting,
    MdCreate,
    MdChat,
    MdVideocam,
    MdDeveloperBoard,
    MdOutlineManageSearch,
    MdBook,
} from "react-icons/md";
import { FaChartPie } from "react-icons/fa";

import BaseLink from "~/components/links/BaseLink";

import FaIcon from "~/components/FaIcon";

import { h3 } from "~/styles/typographyStyles";

import transition from "~/utilities/transition";
import { getLocalStorageItem } from "~/utilities/localStorageHelpers";
import {
    sanitizeAndParseHTML,
    getTopicNameTranslation,
} from "~/utilities/helpers";

import useStringTranslation from "~/hooks/translations/useStringTranslation";
import trackCustomEvent from "~/utilities/trackCustomEvent";
import { ScreenReaderText } from "../styles/screenReaderText";

export const Wrapper = styled.article`
    ${transition({}, "background-color")};
    position: relative;
    background-color: ${({ theme }) =>
        theme.colors.backgroundColor.defaultLight};
    border: ${({ theme }) => `1px solid ${theme.palette.borderGray}`};
    width: 100%;
    padding: 30px 30px 0;
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
`;

export const ArticleLink = styled(BaseLink)`
    ${transition({}, "text-decoration")};
    color: currentColor;

    &::before {
        ${transition({}, "background-color")};
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 1;
    }

    > span {
        position: relative;
        z-index: 2;
    }

    &:hover,
    &:focus {
        text-decoration: underline;

        &:before {
            background-color: ${({ theme }) =>
                theme.colors.backgroundColor.section};
        }
    }
`;

export const MainContent = styled.div`
    margin-bottom: auto;
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 15px;
`;

const HeaderLink = styled(BaseLink)`
    ${transition({}, "color")};
    color: ${({ theme }) => theme.palette.purple};
    font-weight: 700;
    position: relative;
    z-index: 2;
    font-size: 1.8rem;
    line-height: 1.2;
    display: inline-block;

    &:hover,
    &:focus {
        color: ${({ theme }) => theme.palette.black};
        text-decoration: underline;
    }
`;

const HeaderLeft = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    flex: 1;
`;

const HeaderRight = styled.div`
    margin-left: 10px;
    color: ${({ theme }) => theme.palette.grayTwo};
    font-size: 1.6rem;
`;

const LockedIcon = styled(FaIcon)`
    position: relative;
    z-index: 2;
`;

export const Title = styled.h3`
    ${h3};
    font-weight: 700;
`;

export const Excerpt = styled.div`
    font-size: 1.8rem;
    position: relative;
    pointer-events: none;
    z-index: 2;
`;

const Footer = styled.div`
    margin-top: 30px;
    padding: 20px 0;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
`;

const TypeLink = styled(BaseLink)`
    position: relative;
    z-index: 2;
    font-size: 1.8rem;
    color: ${({ theme }) => theme.palette.black};
    flex: 1 1 100%;
    text-align: left;
    line-height: 1;
    margin-top: 15px;

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletPortrait}) {
        margin-top: 0;
        text-align: right;
        flex: 0 1 auto;
    }
`;

const TypeLabel = styled.span`
    ${TypeLink} &:hover,
    ${TypeLink} &:focus {
        text-decoration: underline;
    }
`;

const TypeIconWrapper = styled.span`
    color: ${({ theme }) => theme.palette.purple};
    margin-right: 5px;
    white-space: nowrap;
    font-size: 2rem;
`;

const ReadMore = styled(BaseLink)`
    ${transition({}, "color")};
    color: ${({ theme }) => theme.colors.typography.links};
    font-weight: 700;
    line-height: 1;
    margin-right: 10px;
    position: relative;
    z-index: 2;
    pointer-events: none;

    ${Wrapper}:hover &,
    ${Wrapper}:focus & {
        color: ${({ theme }) => theme.colors.typography.linksHover};
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
        flex: 1 0 auto;
    }
`;

const IconWrapper = styled.span`
    display: inline;
    white-space: nowrap;
    vertical-align: middle;
`;

const ReadMoreIcon = styled(FaIcon)`
    margin: ${({ gated }) => (gated ? `0 5px 0 0` : `0 0 0 5px`)};
    font-size: ${({ gated }) => (gated ? `1.6rem` : `2rem`)};
`;

export const getTopic = (primaryTopic, topics) => {
    if (primaryTopic) {
        return primaryTopic;
    }

    if (topics.length) {
        return topics[0];
    }

    return null;
};

const getType = (types, languageCode) => {
    const type = types.length ? types[0] : null;

    if (!type || languageCode === "en") {
        return type;
    }

    const value = find(type.translatedName, { languageCode });
    const linkLabel = value?.translation || type.linkLabel;

    return {
        ...type,
        linkLabel,
    };
};

const getReadMore = ({ videoUrl, isComingSoonArticle, ctaCopyOverride }) => {
    if (ctaCopyOverride) {
        return { key: ctaCopyOverride, text: ctaCopyOverride };
    }

    if (isComingSoonArticle) {
        return { key: "register_strings_coming_soon", text: "Coming Soon" };
    }

    if (videoUrl) {
        return { key: "register_strings_view_now", text: "View Now" };
    }

    return {
        key: "register_strings_continue_reading",
        text: "Continue Reading",
    };
};

const useGetReadMoreText = ({
    videoUrl,
    ctaCopyOverride,
    isComingSoonArticle,
    languageCode,
}) => {
    const readMore = getReadMore({
        videoUrl,
        ctaCopyOverride,
        isComingSoonArticle,
    });

    const strings = [
        { text: readMore.text, handle: "readMoreText", key: readMore.key },
    ];

    const { readMoreText } = useStringTranslation(strings, languageCode);

    return readMoreText;
};

const getTypeIcon = (slug, iconClass) => {
    const iconSlugMap = {
        "technical-articles": MdPermDataSetting,
        webinars: MdVideocam,
        "in-the-studio": MdVideocam,
        blog: MdChat,
        "basics-of-silicon-carbide": MdDeveloperBoard,
        "white-paper": MdCreate,
        infographic: FaChartPie,
        "case-study": MdOutlineManageSearch,
    };

    const iconClassMap = {
        perm_data_setting: MdPermDataSetting,
        videocam: MdVideocam,
        chat: MdChat,
        developer_board: MdDeveloperBoard,
        create: MdCreate,
        chart_pie: FaChartPie,
        outline_manage_search: MdOutlineManageSearch,
    };

    let icon = MdBook;

    if (has(iconClassMap, iconClass)) {
        icon = iconClassMap[iconClass];
    } else if (has(iconSlugMap, slug)) {
        icon = iconSlugMap[slug];
    }

    return icon;
};

const handleTopicNameTranslation = (topic, topics, languageCode) => {
    const translatedName = getTopicNameTranslation(
        topic?.name,
        topics,
        languageCode,
    );

    let translatedTopic = topic;

    if (translatedName && topic) {
        translatedTopic = { ...topic, name: translatedName.translation };
    }

    return translatedTopic;
};

export const CardHeader = ({
    gated,
    isGated,
    primaryTopic,
    topics,
    languageCode,
}) => {
    const topic = handleTopicNameTranslation(
        getTopic(primaryTopic, topics),
        topics,
        languageCode,
    );

    const { lockedArticleText, unlockedArticleText } = useStringTranslation([
        { text: "Locked Article", handle: "lockedArticleText" },
        { text: "Unlocked Article", handle: "unlockedArticleText" },
    ]);

    return (
        <Header>
            <HeaderLeft>
                {topic ? (
                    <HeaderLink to={topic.linkUrl}>{topic.name}</HeaderLink>
                ) : null}
            </HeaderLeft>
            {gated ? (
                <HeaderRight>
                    <ScreenReaderText>
                        {isGated ? lockedArticleText : unlockedArticleText}
                    </ScreenReaderText>
                    <LockedIcon name={isGated ? "lock" : "lock-open"} />
                </HeaderRight>
            ) : null}
        </Header>
    );
};

export const CardFooter = ({
    link,
    small,
    ctaCopyOverride,
    videoUrl,
    gated,
    types,
    isComingSoonArticle,
    languageCode,
}) => {
    const type = getType(types, languageCode);
    const TypeIcon = getTypeIcon(type?.slug, type?.iconClass);

    const readMoreText = useGetReadMoreText({
        videoUrl,
        ctaCopyOverride,
        isComingSoonArticle,
        languageCode,
    });

    return type ? (
        <Footer>
            {!small ? (
                <ReadMore to={link} aria-hidden="true">
                    {readMoreText}
                    <IconWrapper>
                        &nbsp;
                        <ReadMoreIcon {...{ gated }} name="chevron-right" />
                    </IconWrapper>
                </ReadMore>
            ) : null}
            <TypeLink to={type.linkUrl}>
                {TypeIcon ? (
                    <TypeIconWrapper>
                        <TypeIcon style={{ verticalAlign: "middle" }} />
                        &nbsp;
                    </TypeIconWrapper>
                ) : null}
                <TypeLabel>{type.linkLabel}</TypeLabel>
            </TypeLink>
        </Footer>
    ) : null;
};

export const MainBottom = ({
    link,
    excerpt,
    small,
    gated,
    ctaCopyOverride,
    isComingSoonArticle,
    videoUrl,
    languageCode,
}) => {
    const readMoreText = useGetReadMoreText({
        videoUrl,
        ctaCopyOverride,
        isComingSoonArticle,
        languageCode,
    });

    return (
        <>
            {small ? (
                <ReadMore to={link} ariaHidden="true">
                    {readMoreText}
                    <IconWrapper>
                        &nbsp;
                        <ReadMoreIcon {...{ gated }} name="chevron-right" />
                    </IconWrapper>
                </ReadMore>
            ) : null}

            {excerpt && !small ? (
                <Excerpt>{sanitizeAndParseHTML(excerpt)}</Excerpt>
            ) : null}
        </>
    );
};

export const trackKcCardClick = (
    title,
    link,
    primaryTopic = "",
    types = [],
    languageCode = "",
) => {
    const type =
        types?.length && languageCode ? getType(types, languageCode) : "";

    trackCustomEvent("gaEvent", {
        label: title,
        action: "KC Article Card Click",
        category: primaryTopic?.linkLabel,
        value: link,
        additionalTracking: {
            eventType: type?.linkLabel,
        },
    });
};

const KnowledgeCenterCard = ({
    excerpt = "",
    title,
    types = [],
    topics = [],
    primaryTopic = null,
    url,
    gated = false,
    urlOverride = "",
    openUrlOverrideInANewTab = false,
    ctaCopyOverride = "",
    videoUrl = "",
    small = false,
    languageCode,
    isComingSoonArticle = false,
    translationGroupId,
}) => {
    const [isGated, setIsGated] = useState(gated);
    const link = urlOverride ? urlOverride : url;

    const ARTICLE_LOCAL_STORAGE_NAME = `kc-${translationGroupId}`;

    useEffect(() => {
        const gatedFormHasBeenSubmitted = !!getLocalStorageItem(
            ARTICLE_LOCAL_STORAGE_NAME,
        );

        setIsGated(!gatedFormHasBeenSubmitted);
    }, [setIsGated, ARTICLE_LOCAL_STORAGE_NAME]);

    return (
        <Wrapper>
            <MainContent>
                <CardHeader
                    {...{
                        gated,
                        isGated,
                        primaryTopic,
                        topics,
                        languageCode,
                    }}
                />
                <Title>
                    <ArticleLink
                        to={link}
                        external={!!urlOverride && openUrlOverrideInANewTab}
                        onClick={() =>
                            trackKcCardClick(
                                title,
                                link,
                                primaryTopic,
                                types,
                                languageCode,
                            )
                        }
                    >
                        <span>{sanitizeAndParseHTML(title)}</span>
                    </ArticleLink>
                </Title>
                <MainBottom
                    {...{
                        link,
                        excerpt,
                        small,
                        gated,
                        ctaCopyOverride,
                        videoUrl,
                        isComingSoonArticle,
                        languageCode,
                    }}
                />
            </MainContent>
            <CardFooter
                {...{
                    link,
                    small,
                    ctaCopyOverride,
                    videoUrl,
                    gated,
                    types,
                    isComingSoonArticle,
                    languageCode,
                }}
            />
        </Wrapper>
    );
};

KnowledgeCenterCard.propTypes = {
    id: PropTypes.string.isRequired,
    topics: PropTypes.arrayOf(PropTypes.shape({})),
    excerpt: PropTypes.string,
    title: PropTypes.string.isRequired,
    types: PropTypes.arrayOf(PropTypes.shape({})),
    primaryTopic: PropTypes.shape({}),
    url: PropTypes.string.isRequired,
    gated: PropTypes.bool,
    urlOverride: PropTypes.string,
    openUrlOverrideInANewTab: PropTypes.bool,
    ctaCopyOverride: PropTypes.string,
    videoUrl: PropTypes.string,
    small: PropTypes.bool,
    isComingSoonArticle: PropTypes.bool,
};

export default KnowledgeCenterCard;
